import React from 'react';
import { Card, Stack, Text, Group, Alert, Box } from '@mantine/core';
import { DiagnosticReport } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import classes from './ReviewStatusCard.module.css';

// -----------------------------------------------------------------------------
interface ReviewStatusData {
  allApproved: boolean;
  totalTasks: number;
  approvedTasks: number;
  pendingTasks: number;
  approvedReviewerIds: string[];
  pendingReviewerIds: string[];
}

// -----------------------------------------------------------------------------
interface ReviewStatusCardProps {
  diagnosticReport: DiagnosticReport;
  reviewStatusData: ReviewStatusData | null;
}

// -----------------------------------------------------------------------------
export const ReviewStatusCard: React.FC<ReviewStatusCardProps> = ({
  diagnosticReport,
  reviewStatusData,
}) => {
  const { t } = useTranslation('lims');

  // ---------------------------------------------------------------------------
  if (diagnosticReport.status !== 'preliminary') {
    return null;
  }

  // ---------------------------------------------------------------------------
  return (
    <Card shadow="sm" padding="md" withBorder>
      <Stack gap="md">
        <Text size="lg" fw={600}>
          {t('review.status.title')}
        </Text>

        {reviewStatusData ? (
          reviewStatusData.totalTasks > 0 ? (
            <>
              <Group justify="space-between">
                <Text size="sm">{t('review.status.progress')}</Text>
                <Text size="sm" fw={600}>
                  {t('review.status.approvedOf', {
                    approved: reviewStatusData.approvedTasks,
                    total: reviewStatusData.totalTasks,
                  })}
                </Text>
              </Group>

              <Box
                className={classes.progressBarBorderRadius}
                pos={'relative'}
                h="8px"
                bg={'#e9ecef'}
              >
                <Box
                  className={cx(
                    classes.progressBar,
                    classes.progressBarBorderRadius
                  )}
                  h="100%"
                  bg={reviewStatusData.allApproved ? '#51cf66' : '#74c0fc'}
                  w={`${(reviewStatusData.approvedTasks / reviewStatusData.totalTasks) * 100}%`}
                />
              </Box>

              {reviewStatusData.allApproved ? (
                <Alert color="green" variant="light">
                  <Text size="sm" fw={600}>
                    {t('review.status.allApprovedTitle')}
                  </Text>
                  <Text size="sm">{t('review.status.allApprovedDescription')}</Text>
                </Alert>
              ) : (
                <div>
                  <Text size="sm" c="dimmed" mb="xs">
                    {t('review.status.waitingForApproval', {
                      count: reviewStatusData.pendingTasks,
                    })}
                  </Text>
                  {reviewStatusData.approvedReviewerIds.length > 0 && (
                    <Text size="xs" c="green" mb="xs">
                      {t('review.status.approvedBy', {
                        reviewers: reviewStatusData.approvedReviewerIds.join(', '),
                      })}
                    </Text>
                  )}
                  {reviewStatusData.pendingReviewerIds.length > 0 && (
                    <Text size="xs" c="dimmed">
                      {t('review.status.pendingFrom', {
                        reviewers: reviewStatusData.pendingReviewerIds.join(', '),
                      })}
                    </Text>
                  )}
                </div>
              )}
            </>
          ) : (
            <Alert color="yellow" variant="light">
              <Text size="sm" fw={600}>
                {t('review.status.noTasksTitle')}
              </Text>
              <Text size="sm">{t('review.status.noTasksDescription')}</Text>
            </Alert>
          )
        ) : (
          <Alert color="blue" variant="light">
            <Text size="sm">{t('review.status.loading')}</Text>
          </Alert>
        )}
      </Stack>
    </Card>
  );
};
